import { useRef, useMemo, useEffect, useState, useCallback } from 'react';
import domtoimage from 'dom-to-image-more';
import { useApp } from '../context/AppContext';
import { archetypes } from '../data/archetypes';
import { encodeAnswers } from '../utils/codeUtils';
import styles from './Share.module.css';

// 海报专用短文案
const shortTexts = {
  outlaw: {
    feature: "轨道高度椭圆，被降级仍具影响力，颠覆规则、打破旧秩序",
    alienation: "为反抗而反抗，在社交平台刻意挑衅主流，用极端言论获取关注，陷入愤怒成瘾循环",
    vacation: "出售所有社交APP，试着养一盆植物",
  },
  magician: {
    feature: "甲烷大气如梦似幻，灵感漫溢，善于编织幻觉与愿景",
    alienation: "沉溺于自我欺骗，逃避现实，用虚假人设骗取共鸣，迷失在多重身份中",
    vacation: "关掉滤镜拍一张真实的自拍，去海边看一场没有手机的日落",
  },
  hero: {
    feature: "地表铁锈红，象征战斗与行动力，直面挑战，捍卫信念",
    alienation: "好斗成性，将一切关系视为竞争，习惯性否定他人，陷入孤独胜利循环",
    vacation: "参加一场不记名的互助小组，学习陶艺（允许失败）",
  },
  lover: {
    feature: "浓厚大气包裹，象征爱与美，追求亲密与感官愉悦",
    alienation: "过度依赖他人认可，在关系中失去自我，用讨好换取虚假安全感",
    vacation: "独自去一家从未去过的餐厅，点一份完全凭直觉选的菜",
  },
  caregiver: {
    feature: "唯一已知生命家园，滋养与包容，善于付出与守护",
    alienation: "过度牺牲自我，用付出控制他人，内心积累隐形怨恨",
    vacation: "预约一次按摩，全天不对任何人说「需要帮忙吗？」",
  },
  jester: {
    feature: "橄榄球状，快速自转，象征幽默、灵动与打破常规",
    alienation: "用笑话逃避真实情绪，嘲讽一切深刻，人际关系浮于表面",
    vacation: "写一封不搞笑的手写信，安静地读完一本哲学漫画",
  },
  everyman: {
    feature: "小行星带最大天体，象征归属感与务实，追求平凡中的温暖",
    alienation: "过度迎合群体，害怕独特，在平庸中消磨自我价值",
    vacation: "报名一次单人徒步，不带任何社交装备",
  },
  creator: {
    feature: "侧躺运行，象征颠覆性创新与独立精神，打破框架",
    alienation: "为不同而不同，作品脱离共鸣，陷入孤芳自赏的创作闭环",
    vacation: "模仿一件经典作品（并承认它是模仿）",
  },
  ruler: {
    feature: "质量巨大，象征扩张与掌控，善于规划与影响他人",
    alienation: "控制欲膨胀，忽视他人自主性，用「为你好」包装霸权",
    vacation: "体验一次「盲选旅行」，全程让他人决定行程",
  },
  sage: {
    feature: "冰晶环带象征秩序与积淀，追求真理与体系，沉稳深远",
    alienation: "过于理性而压抑情感，用教条审判一切，人际疏离",
    vacation: "看一部无逻辑的荒诞喜剧，允许自己笑出声",
  },
  explorer: {
    feature: "冥外天体，象征远方与自由，永不停止寻找生命意义",
    alienation: "逃避承诺与停留，将「在路上」作为逃避成长的借口",
    vacation: "在同一家咖啡馆坐一整天，观察窗外同一条街",
  },
  innocent: {
    feature: "冰壳之下或有海洋，象征纯真、信任与乐观",
    alienation: "过于轻信，拒绝面对阴暗，容易陷入虚假美好的陷阱",
    vacation: "看一部结局不完美的电影，不找解析",
  },
};

export default function Share() {
  const { state, dispatch } = useApp();
  const cardRef = useRef(null);
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // 生成用户代码
  const myCode = state.answers.length === 10 ? encodeAnswers(state.answers) : null;

  const handleCopyCode = () => {
    if (myCode) {
      navigator.clipboard.writeText(myCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const { primary, shortText } = useMemo(() => {
    const sorted = Object.entries(state.scores).sort((a, b) => b[1] - a[1]);
    const key = sorted[0] ? sorted[0][0] : 'outlaw';
    return {
      primary: archetypes[key],
      shortText: shortTexts[key],
    };
  }, [state.scores]);

  useEffect(() => {
    const generated = Array.from({ length: 150 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      delay: Math.random() * 3,
      duration: Math.random() * 2 + 1,
    }));
    setStars(generated);

    const meteorList = Array.from({ length: 3 }, (_, i) => ({
      id: i,
      x: Math.random() * 60 + 20,
      y: Math.random() * 20,
      delay: Math.random() * 6 + i * 3,
      duration: Math.random() * 0.8 + 0.6,
    }));
    setMeteors(meteorList);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);

    try {
      const node = cardRef.current;
      const scale = 2;
      const style = {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: node.offsetWidth + 'px',
        height: node.offsetHeight + 'px',
      };

      const param = {
        height: node.offsetHeight * scale,
        width: node.offsetWidth * scale,
        quality: 1,
        style,
        filter: (domNode) => {
          if (domNode.dataset && domNode.dataset.meteor) {
            return false;
          }
          return true;
        },
      };

      const dataUrl = await domtoimage.toPng(node, param);

      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `星蚀档案-${primary.name}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('生成失败', err);
      alert('下载失败: ' + err.message);
    } finally {
      setDownloading(false);
    }
  }, [downloading, primary]);

  const handleRestart = () => {
    dispatch({ type: 'RESET' });
    dispatch({ type: 'SET_PAGE', payload: 'quiz' });
  };

  const handleBack = () => {
    dispatch({ type: 'SET_PAGE', payload: 'result' });
  };

  return (
    <div className={styles.container}>
      {/* 背景装饰 */}
      <div className={styles.bgDecorations}>
        {/* 背景星星 */}
        <div className={styles.bgStars}>
          {Array.from({ length: 80 }).map((_, i) => (
            <div
              key={i}
              className={styles.bgStar}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                width: `${1 + Math.random() * 2}px`,
                height: `${1 + Math.random() * 2}px`,
              }}
            />
          ))}
        </div>
        {/* 装饰星球 */}
        <div className={styles.bgPlanet} data-pos="left" />
        <div className={styles.bgPlanet} data-pos="right" />
        {/* 轨道线 */}
        <div className={styles.bgOrbit} data-orbit="1" />
        <div className={styles.bgOrbit} data-orbit="2" />
        {/* 星座连线 */}
        <svg className={styles.constellation} viewBox="0 0 200 150">
          <circle cx="20" cy="30" r="2" fill="rgba(255,255,255,0.6)" />
          <circle cx="50" cy="20" r="1.5" fill="rgba(255,255,255,0.4)" />
          <circle cx="70" cy="50" r="2" fill="rgba(255,255,255,0.5)" />
          <circle cx="40" cy="70" r="1.5" fill="rgba(255,255,255,0.4)" />
          <line x1="20" y1="30" x2="50" y2="20" stroke="rgba(150,130,200,0.3)" strokeWidth="0.5" />
          <line x1="50" y1="20" x2="70" y2="50" stroke="rgba(150,130,200,0.3)" strokeWidth="0.5" />
          <line x1="70" y1="50" x2="40" y2="70" stroke="rgba(150,130,200,0.3)" strokeWidth="0.5" />
        </svg>
        <svg className={styles.constellation} data-pos="right" viewBox="0 0 200 150">
          <circle cx="180" cy="120" r="2" fill="rgba(255,255,255,0.5)" />
          <circle cx="150" cy="100" r="1.5" fill="rgba(255,255,255,0.4)" />
          <circle cx="160" cy="70" r="2" fill="rgba(255,255,255,0.6)" />
          <line x1="180" y1="120" x2="150" y2="100" stroke="rgba(150,130,200,0.3)" strokeWidth="0.5" />
          <line x1="150" y1="100" x2="160" y2="70" stroke="rgba(150,130,200,0.3)" strokeWidth="0.5" />
        </svg>
      </div>

      <button className={styles.backBtn} onClick={handleBack}>
        ← 返回结果
      </button>
      <div className={styles.cardWrapper}>
        <div className={styles.cardInner}>
          {/* 卡片背面 */}
          <div className={styles.cardBack}>
            <img src="/images/kapaibeimian.png" alt="" className={styles.cardBackImg} />
          </div>
          {/* 卡片正面 */}
          <div ref={cardRef} className={styles.card} data-card>
          {/* 星空背景 */}
          <div className={styles.starsContainer}>
            {stars.map((star) => (
              <div
                key={star.id}
                className={styles.star}
                style={{
                  left: `${star.x}%`,
                  top: `${star.y}%`,
                  width: `${star.size}px`,
                  height: `${star.size}px`,
                  animationDelay: `${star.delay}s`,
                  animationDuration: `${star.duration}s`,
                }}
              />
            ))}
            {meteors.map((meteor) => (
              <div
                key={`meteor-${meteor.id}`}
                className={styles.meteor}
                data-meteor
                style={{
                  left: `${meteor.x}%`,
                  top: `${meteor.y}%`,
                  animationDelay: `${meteor.delay}s`,
                  animationDuration: `${meteor.duration}s`,
                }}
              />
            ))}
          </div>

          {/* 顶部标题区 */}
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <h1 className={styles.planetName}>{primary.planet}</h1>
              <div className={styles.subtitleRow}>
                <span className={styles.dot} />
                <span className={styles.archetypeName}>{primary.name}</span>
                <span className={styles.dividerLine} />
                <span className={styles.label}>荣格十二原型</span>
              </div>
            </div>
            {/* 右上装饰 */}
            <img src="/images/saturn.png" alt="" className={styles.saturnImg} />
          </div>

          {/* 主星球区域 */}
          <div className={styles.planetArea}>
            {/* 轨道环 - 使用SVG确保导出正常 */}
            <svg className={styles.orbitSvg} viewBox="0 0 400 400">
              <circle cx="200" cy="200" r="160" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <circle cx="200" cy="200" r="190" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="4 4" />
            </svg>
            <img src={primary.planetImg} alt={primary.planet} className={styles.mainPlanetImg} />
            <img src="/images/small-planet.png" alt="" className={styles.smallPlanetImg} />
            {/* 右侧竖排文字 */}
            <div className={styles.verticalText}>
              <span>ARCHETYPE</span>
            </div>
          </div>

          {/* 底部信息区 */}
          <div className={styles.footer}>
            {/* 特征 */}
            <div className={styles.featureBox}>
              <p className={styles.featureText}>{shortText.feature}</p>
            </div>
            
            {/* 两栏信息 */}
            <div className={styles.infoRow}>
              <div className={styles.infoBox}>
                <h3>异化信号</h3>
                <p>{shortText.alienation}</p>
              </div>
              <div className={styles.infoBox}>
                <h3>疗愈处方</h3>
                <p>{shortText.vacation}</p>
              </div>
            </div>
          </div>

          {/* 装饰线条 */}
          <div className={styles.decorLines}>
            <div className={styles.line1} />
            <div className={styles.line2} />
          </div>

          {/* 左侧装饰星球 */}
          <img src="/images/planets/venus.png" alt="" className={styles.decorPlanet} />
          
          {/* 角落装饰 */}
          <div className={styles.cornerTL} />
          <div className={styles.cornerBR} />
          
          {/* 坐标装饰 */}
          <div className={styles.coordinates}>
            <span>RA 12h 30m</span>
            <span>DEC +41° 54'</span>
          </div>
          
          {/* 用户代码 */}
          {myCode && (
            <div className={styles.userCode}>
              <span className={styles.codePrefix}>星球代码</span>
              <span className={styles.codeValue}>{myCode}</span>
            </div>
          )}
          </div>
          {/* card 结束 */}
        </div>
        {/* cardInner 结束 */}
      </div>
      {/* cardWrapper 结束 */}

      <div className={styles.buttons}>
        {myCode && (
          <button className={styles.codeBtn} onClick={handleCopyCode}>
            {codeCopied ? '已复制 ✓' : `复制代码 ${myCode}`}
          </button>
        )}
        <button className={styles.primary} onClick={handleDownload} disabled={downloading}>
          {downloading ? '生成中...' : '下载分享卡片'}
        </button>
        <button className={styles.secondary} onClick={handleRestart}>
          再测一次
        </button>
      </div>
    </div>
  );
}
